import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SessionContext from 'contexts/session/context';
import { deleteSession, getSession } from 'requests/session';
import { deleteAuthorizationToken, deleteAdminAuthorizationToken, setAuthorizationToken, getAuthorizationToken } from 'services/authorization_token';
import { deleteActiveGroupCookie } from 'contexts/active_group/provider';
import { Helmet } from 'react-helmet';
import { DEVELOPMENT } from 'constants';
import { useCurrentBrand } from 'contexts/current_brand/use_current_brand';
import isEmpty from 'lodash/isEmpty';
import { setRedirectAfterSignIn } from 'services/redirect_after_sign_in';
import { useI18n } from 'hooks/use_i18n';
import { useExternalServices } from 'hooks/use_external_services';
import Loader from 'components/ui/loader';
import { useShare } from 'contexts/share/use_share';
import { useApplication } from 'hooks/use_application';
import sessionStore from 'services/session_store';
import BackProvider from '../../components/back/provider';
import { getRequestSession, getTemporarySessionByToken } from 'requests/session';

const SessionProvider = ({ children }) => {
  const sessionRef = useRef(null);
  const [session, setSession] = useState({});
  const [loadingSession, setLoadingSession] = useState(true);
  const { setCurrentLocale } = useI18n();
  const { identifyUser, closeIntercom } = useExternalServices();
  const { currentBrand } = useCurrentBrand();
  const authorizations = session.authorizations;
  const scripts = currentBrand && currentBrand.html_script && JSON.parse(currentBrand.html_script);
  const { shareSession, shareSessionToken } = useShare();
  const { buildTimestamp } = useApplication();
  const [isNewNotification, setIsNewNotification] = useState(false);
  const keepSession = (session) => {
    setSession(session);
    setCurrentLocale(session.user.locale);
    identifyUser(session.user, session.is_provider);
    sessionRef.current = session;
    setLoadingSession(false);
  };

  const getAndKeepSession = () => {
    return getSession()
      .then((response) => {
        setIsNewNotification({
          has_unread_notifications: response.data.user.has_unread_notifications,
          unread_notifications_count: response.data.user.unread_notifications_count,
        });
        setAuthorizationToken(response.headers.authorization?.split(' ')[1]);
        keepSession(response.data);
        setLoadingSession(false);
      })
      .catch((e) => {
        console.error('e', e);
        setLoadingSession(true);
      });
  };

  const fetchSession = () => {
    const requestUserToken = sessionStore.get('rut');
    const temporaryUserToken = sessionStore.get('tut');
    if (!isEmpty(temporaryUserToken)) {
      return getTemporarySessionByToken(temporaryUserToken)
        .then((response) => {
          setAuthorizationToken(response.headers.authorization?.split(' ')[1]);
          keepSession(response.data.session);
          setLoadingSession(false);
          sessionStore.delete('tut');
        })
        .catch((e) => {
          sessionStore.delete('tut');
          console.error('e', e);
          setLoadingSession(true);
        });
    }
    if (!isEmpty(requestUserToken)) {
      return getRequestSession(requestUserToken)
        .then((response) => {
          setAuthorizationToken(response.headers.authorization?.split(' ')[1]);
          keepSession(response.data.session);
          setLoadingSession(false);
          sessionStore.delete('rut');
        })
        .catch((e) => {
          sessionStore.delete('rut');
          console.error('e', e);
          setLoadingSession(true);
        });
    }
    if (!isEmpty(shareSession) && !isEmpty(shareSessionToken)) {
      setAuthorizationToken(shareSessionToken);
      keepSession(shareSession);
      setLoadingSession(false);
      return;
    }

    if (!getAuthorizationToken()) {
      setRedirectAfterSignIn(window.location.href);
      return setLoadingSession(false);
    }
    if (isEmpty(session) && isEmpty(sessionRef.current)) {
      return getAndKeepSession();
    }
    setLoadingSession(false);
  };

  const signOut = (event) => {
    event.preventDefault();
    deleteSession()
      .then(() => {
        deleteAuthorizationToken();
        deleteAdminAuthorizationToken();
        deleteActiveGroupCookie();
        sessionStore.delete('request_index_filters');
        setSession({});
        closeIntercom();
        sessionStore.delete('rut');
      })
      .catch(() => {
        deleteAuthorizationToken();
        deleteAdminAuthorizationToken();
        deleteActiveGroupCookie();
        sessionStore.delete('request_index_filters');
        closeIntercom();
        sessionStore.delete('rut');
      });
  };

  const updateCurrentUser = (currentUser) => {
    setSession((prevSession) => {
      return {
        ...prevSession,
        user: currentUser,
      };
    });
  };

  useEffect(() => {
    fetchSession();
  }, []);

  if (loadingSession) {
    return <Loader />;
  }

  return (
    <SessionContext.Provider
      value={{
        keepSession,
        getSession,
        getAndKeepSession: () => {
          return getAndKeepSession();
        },
        fetchSession,
        currentUser: session.user
          ? {
              ...session.user,
              appReleaseTimestamp: buildTimestamp,
            }
          : null,
        accessibleGroups: session.accessible_groups,
        currentSellerAccount: session.seller_account,
        currentGroup: session.group,
        currentSession: session,
        currentAccount: session.currentAccount,
        authorizations,
        isProvider: session.is_provider,
        isAnonymous: session.user?.anonymous,
        isSuperAdmin: session.user?.superadmin,
        isDev: ['christophe@insurgate.com', 'maxime@insurgate.com', 'joel@insurgate.com', 'kathleen@insurgate.com', 'fabrizio@insurgate.com'].includes(
          session.user?.email,
        ),
        isAgent: session.user?.account?.is_agent,
        currentCompanyAccount: session.company_account,
        pendingIntegrationsCount: session.pending_integrations_count,
        signOut,
        currentBrand,
        updateCurrentUser,
        isNewNotification,
        setIsNewNotification,
      }}
    >
      {scripts && scripts.length > 0 && !DEVELOPMENT && (
        <Helmet>
          {scripts.map((script, id) => (
            <script {...script} key={id} />
          ))}
        </Helmet>
      )}

      <BackProvider
        channel={'NotificationsChannel'}
        channelParams={{}}
        onConnected={() => {
          console.log('Backprovider Connected');
        }}
        onDisconnected={() => {
          console.log('Backprovider Disconnected');
        }}
        onReceived={(data) => {
          getAndKeepSession();
        }}
      />
      {children}
    </SessionContext.Provider>
  );
};

SessionProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]).isRequired,
};

export default SessionProvider;
